import { useCallback, useEffect, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import { useAihubTokenStatus } from 'features/load-hub/hooks/useAihubTokenStatus'
import { trackBoxTruckUiInteraction } from '../utils/analytics'

/**
 * The board's BeOnCarrier connection: whether this project has one, and the modal that makes one.
 *
 * Separated from the board because it is the one part of the BEON feed that has nothing to do with the
 * board — it asks about the PROJECT. The switch itself stays on the page beside its DAT twin, where the
 * gated-source bookkeeping it shares with DAT lives.
 *
 * `connected` is a tri-state. `null` means the status read has not come back, and callers must not treat
 * that as "no connection": gating the feed on an answer we do not have would drop it on the first search of
 * a board whose operator has BEON on and working, and present that as "no loads". See `beonRequestable`.
 */
export interface BoxTruckBeonConnection {
    connected: boolean | null
    /** The same value a handler can read in the turn it changes it — see `connectedRef`. */
    connectedRef: { readonly current: boolean | null }
    modalOpen: boolean
    openModal: () => void
    closeModal: () => void
    /** Call on a successful connect, BEFORE anything that decides whether to ask for the feed. */
    markConnected: () => void
    checkStatus: ReturnType<typeof useAihubTokenStatus>['checkStatus']
}

export const useBoxTruckBeonConnection = (): BoxTruckBeonConnection => {
    const [connected, setConnected] = useState<boolean | null>(null)
    // The request gate is a ref for the same reason the excluded-source set is: it is read before React has
    // re-rendered with the new value.
    const connectedRef = useRef<boolean | null>(null)
    const [modalOpen, setModalOpen] = useState(false)
    const { checkStatus } = useAihubTokenStatus()

    const remember = useCallback((next: boolean | null) => {
        connectedRef.current = next
        setConnected(next)
    }, [])

    // ONE GET per board mount — `checkStatus` is a stable callback, so this runs exactly once — and again
    // only when a connect succeeds. Not per tab, per page or per search: it answers a question about the
    // project, which no amount of board activity changes.
    useEffect(() => {
        let live = true
        void checkStatus('beon')
            .then((status) => {
                if (live) remember(status?.isConnected === true)
            })
            .catch((cause) => {
                // Sentry, not `console` and not `utils/logger` (which is only a console wrapper, so neither
                // would leave a trace anywhere anyone looks). This is the convention the rest of this feature
                // already uses — see useBoxTruckMap.
                //
                // Reported because the state stays `null`, which is honest — we learned nothing — but what
                // the row then shows depends on the switch: off, it offers Connect, which is wrong for an
                // operator who IS connected. Self-correcting rather than a dead end, since the modal
                // re-checks on open and reports success on its first poll; a persistently failing read,
                // though, leaves them looking at the wrong control with nothing on screen to explain it.
                Sentry.captureException(cause, { tags: { feature: 'box-truck-beon', operation: 'read-connection-status' } })
            })
        return () => {
            live = false
        }
    }, [checkStatus, remember])

    const openModal = useCallback(() => {
        trackBoxTruckUiInteraction('beon_connect_opened')
        setModalOpen(true)
    }, [])

    const closeModal = useCallback(() => setModalOpen(false), [])
    const markConnected = useCallback(() => remember(true), [remember])

    return { connected, connectedRef, modalOpen, openModal, closeModal, markConnected, checkStatus }
}
